import { Dialog, DialogActions, DialogContent } from "@mui/material"
import type { FC } from "react"
import { FormProvider, useForm } from "react-hook-form"

import { toInvoiceFormValues } from "../../utils"
import { InvoiceForm } from "../InvoiceForm/InvoiceForm"

import { Button } from "@/components/common/Button"
import DialogHeader from "@/components/common/Modals/DialogHeader"
import type { Invoice } from "@/graphql/codegen/graphql"

interface InvoiceReviewDialogProps {
  invoice: Invoice
  onClose: () => void
}

// Standalone read-only preview of an invoice, used from the duplicate warning modal to let
// the user compare a matching invoice against the one being created. Distinct from
// `InvoiceFormDialog` (mode='view') because that component wires the full create/update/upload
// mutation stack via `useInvoiceForm` — reusing it would either recurse or spawn unused
// mutation hooks in the review flow.
export const InvoiceReviewDialog: FC<InvoiceReviewDialogProps> = ({ invoice, onClose }) => {
  const methods = useForm({ defaultValues: toInvoiceFormValues(invoice) })

  return (
    <Dialog open onClose={onClose} maxWidth='md' fullWidth data-testid='invoice.review-dialog'>
      <DialogHeader title='View Invoice' onClose={onClose} />
      <FormProvider {...methods}>
        <DialogContent className='flex flex-col gap-6 pt-4'>
          <InvoiceForm mode='view' invoice={invoice} readOnly />
        </DialogContent>
        <DialogActions className='px-6 pb-6'>
          <Button appearance='outlined' onClick={onClose} data-testid='invoice.review-dialog.close-button'>
            Close
          </Button>
        </DialogActions>
      </FormProvider>
    </Dialog>
  )
}
